import { Button } from '@execonline-inc/execonline-ui';
import { useQueryClient } from '@tanstack/react-query';
import { Maybe } from 'maybeasy';
import { observer } from 'mobx-react';
import * as React from 'react';
import { NotTranslated, T } from '../../../../Translations';
import { launchTestSignIn, serverErrorMessage, updateAttributeMapping } from '../../Api';
import { formatDateTime } from '../../dates';
import { capturedValue, ConnectionResource, defaultClaimFor, diagnosedFields } from '../../Types';
import DiagnosticRow from './DiagnosticRow';
import HowThisWorks from './HowThisWorks';

interface Props {
  resource: ConnectionResource;
  // Present in the standalone diagnostics context (manage page); the wizard
  // omits it because a passing test advances the page on its own.
  onDone: Maybe<() => void>;
}

type FieldMapping = { [field: string]: string };

type State =
  | { kind: 'ready'; mapping: FieldMapping }
  | { kind: 'applying'; mapping: FieldMapping }
  | { kind: 'launching'; mapping: FieldMapping }
  // priorLaunchAtMs remembers which test results were on screen at launch;
  // the waiting state ends when the polled resource carries newer ones.
  | { kind: 'test-launched'; mapping: FieldMapping; priorLaunchAtMs: number }
  | { kind: 'error'; mapping: FieldMapping; message: Maybe<string> };

// Stored mapping first, vendor default as the fallback - so every field
// shows the claim name the IdP is expected to send, even when an earlier
// mapping update dropped it.
const initialMapping = (resource: ConnectionResource): FieldMapping =>
  diagnosedFields.reduce<FieldMapping>((mapping, diagnosed) => {
    const stored = resource.payload.attributeMapping
      .filter((entry) => entry.field === diagnosed.field)
      .map((entry) => entry.claim)
      .join('');
    mapping[diagnosed.field] =
      stored.length > 0 ? stored : defaultClaimFor(resource.payload.vendor, diagnosed.field);
    return mapping;
  }, {});

const AttributesCheck: React.FC<Props> = ({ resource, onDone }) => {
  const [state, setState] = React.useState<State>({
    kind: 'ready',
    mapping: initialMapping(resource),
  });
  const queryClient = useQueryClient();

  const busy = state.kind === 'applying' || state.kind === 'launching';

  // Unsaved claim edits must reach Cognito before a test can reflect them,
  // so the save action also launches the re-test and only shows while the
  // mapping differs from what is stored.
  const storedMapping = initialMapping(resource);
  const dirty = diagnosedFields.some(
    (diagnosed) => (state.mapping[diagnosed.field] || '').trim() !== storedMapping[diagnosed.field],
  );

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['sso-configuration'] });
    queryClient.invalidateQueries({ queryKey: ['sso-configurations'] });
  };

  const lastLaunchAtMs = resource.payload.lastLaunchAt.map((at) => at.getTime()).getOrElseValue(-1);

  const waitingForResults =
    state.kind === 'test-launched' && state.priorLaunchAtMs === lastLaunchAtMs;
  const resultsArrived = state.kind === 'test-launched' && state.priorLaunchAtMs !== lastLaunchAtMs;

  // The connection query only polls during the wizard statuses, and the app
  // disables refetch-on-focus globally - so while a launched test is
  // outstanding (e.g. diagnostics on an active connection), poll here until
  // its results land.
  React.useEffect(() => {
    if (!waitingForResults) {
      return;
    }
    const interval = window.setInterval(() => {
      queryClient.invalidateQueries({ queryKey: ['sso-configuration'] });
    }, 5000);
    return () => window.clearInterval(interval);
  }, [waitingForResults, queryClient]);

  const runTest = () => {
    setState({ kind: 'launching', mapping: state.mapping });
    launchTestSignIn(resource.links).fork(
      (err) =>
        setState({ kind: 'error', mapping: state.mapping, message: serverErrorMessage(err) }),
      () =>
        setState({
          kind: 'test-launched',
          mapping: state.mapping,
          priorLaunchAtMs: lastLaunchAtMs,
        }),
    );
  };

  const applyMappingAndRetest = () => {
    const mapping: FieldMapping = {};
    Object.keys(state.mapping).forEach((field) => {
      const claim = state.mapping[field].trim();
      if (claim.length > 0) {
        mapping[field] = claim;
      }
    });

    setState({ kind: 'applying', mapping: state.mapping });
    updateAttributeMapping(resource.links, mapping).fork(
      (err) =>
        setState({ kind: 'error', mapping: state.mapping, message: serverErrorMessage(err) }),
      () => {
        invalidate();
        launchTestSignIn(resource.links).fork(
          (err) =>
            setState({ kind: 'error', mapping: state.mapping, message: serverErrorMessage(err) }),
          () =>
            setState({
              kind: 'test-launched',
              mapping: state.mapping,
              priorLaunchAtMs: lastLaunchAtMs,
            }),
        );
      },
    );
  };

  const missingRequired = resource.payload.lastLaunchAttributes
    .map((captured) =>
      resource.payload.requiredAttributes.filter((field) =>
        capturedValue(field, captured)
          .map(() => false)
          .getOrElseValue(true),
      ),
    )
    .getOrElseValue([]);

  const allRequiredMatched = resource.payload.lastLaunchAttributes
    .map(() => missingRequired.length === 0)
    .getOrElseValue(false);

  return (
    <>
      {resource.payload.lastError
        .map((lastError) => (
          <div
            key="failure-banner"
            className="mb-6 rounded-lg border border-destructive/50 bg-card p-4"
          >
            <p className="font-semibold text-destructive">
              <T kind="Your last test sign-in did not complete" />
            </p>
            <p className="mt-1 text-sm text-muted-foreground">
              <T kind="The identity provider rejected the sign-in with the following error. Fix the mapping below or adjust the configuration in your IdP, then run another test." />
            </p>
            <p className="mt-2 break-words rounded bg-muted p-2 font-mono text-xs text-foreground">
              <NotTranslated text={lastError} />
            </p>
          </div>
        ))
        .getOrElse(() => (
          <React.Fragment key="failure-banner" />
        ))}
      {resource.payload.lastLaunchAttributes
        .map(() =>
          missingRequired.length > 0 ? (
            <div key="banner" className="rounded-lg border border-border bg-secondary p-4">
              <p className="font-semibold text-foreground">
                <T kind="Some attributes need attention" />
              </p>
              <p className="mt-1 text-sm text-muted-foreground">
                <T kind="Sign-in works, but profiles would be incomplete. Fix the mappings below, or adjust the claims in your IdP and run another test." />
              </p>
            </div>
          ) : (
            <div key="banner" className="rounded-lg border border-border bg-secondary p-4">
              <p className="font-semibold text-foreground">
                <T kind="All required attributes matched" />
              </p>
            </div>
          ),
        )
        .getOrElse(() => (
          <div key="banner" className="rounded-lg border border-border bg-secondary p-4">
            <p className="font-semibold text-foreground">
              <T kind="Run a test sign-in to check the attributes your IdP sends" />
            </p>
            <p className="mt-1 text-sm text-muted-foreground">
              <T kind="The test signs you in through this connection without affecting anyone else - no domains route through it yet." />
            </p>
          </div>
        ))}
      <div className="mt-6 grid grid-cols-1 items-start gap-6 lg:grid-cols-5">
        <div className="lg:col-span-3">
          <h3 className="text-base font-semibold text-foreground">
            {resource.payload.lastLaunchAttributes
              .map(() => <T key="heading" kind="Attributes from your last test sign-in" />)
              .getOrElse(() => (
                <T key="heading" kind="Attributes your IdP needs to send" />
              ))}
          </h3>
          <div className="mt-3 rounded-lg border border-border bg-card p-6">
            <p className="text-sm text-muted-foreground">
              {resource.payload.lastLaunchAttributes
                .map(() => (
                  <React.Fragment key="intro">
                    <T kind="We compare what your IdP sent against what ExecOnline expects." />{' '}
                    {resource.payload.lastLaunchAt
                      .map((at) => (
                        <span key="at">
                          <T kind="Last test" />: {formatDateTime(at)}
                        </span>
                      ))
                      .getOrElse(() => (
                        <></>
                      ))}
                  </React.Fragment>
                ))
                .getOrElse(() => (
                  <T
                    key="intro"
                    kind="Configure your IdP to send the SAML claims below for each attribute, then run a test sign-in to check what arrives."
                  />
                ))}
            </p>
            <div className="mt-4 overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr>
                    <th className="border-b border-border py-2 pr-3 text-left text-sm font-bold text-foreground">
                      <T kind="ExecOnline field" />
                    </th>
                    <th className="border-b border-border py-2 pr-3 text-left text-sm font-bold text-foreground">
                      <T kind="Status" />
                    </th>
                    <th className="border-b border-border py-2 text-left text-sm font-bold text-foreground">
                      <T kind="What your IdP sent" />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {diagnosedFields.map((diagnosed) => (
                    <DiagnosticRow
                      key={diagnosed.field}
                      diagnosed={diagnosed}
                      required={resource.payload.requiredAttributes.includes(diagnosed.field)}
                      captured={resource.payload.lastLaunchAttributes}
                      claim={state.mapping[diagnosed.field] || ''}
                      disabled={busy}
                      onClaimChange={(claim) =>
                        setState({
                          kind: 'ready',
                          mapping: { ...state.mapping, [diagnosed.field]: claim },
                        })
                      }
                    />
                  ))}
                </tbody>
              </table>
            </div>
            {state.kind === 'error' && (
              <p className="mt-3 text-sm text-destructive">
                {state.message
                  .map((text) => <NotTranslated key="server" text={text} />)
                  .getOrElse(() => (
                    <T kind="We are unable to process your request at this time" />
                  ))}
              </p>
            )}
            {waitingForResults && (
              <p className="mt-3 text-sm text-muted-foreground">
                <T kind="Waiting for your test sign-in to finish - complete it in the other tab. This page updates automatically." />
              </p>
            )}
            {resultsArrived && (
              <p className="mt-3 text-sm text-accent">
                <T kind="Test results received - the table above shows the latest attributes." />
              </p>
            )}
            <div className="mt-5 flex flex-wrap justify-end gap-3 border-t border-border pt-4">
              {dirty ? (
                <Button
                  color="success"
                  isLoading={busy}
                  disabled={busy}
                  onClick={applyMappingAndRetest}
                  data-intercom-target="sso-save-mapping-button"
                >
                  <T kind="Save mapping & re-test" />
                </Button>
              ) : (
                <Button
                  color="primary"
                  variant="outlined"
                  isLoading={state.kind === 'launching'}
                  disabled={busy}
                  onClick={runTest}
                  data-intercom-target="sso-run-test-sign-in-button"
                >
                  {resource.payload.lastLaunchAttributes
                    .map(() => <T key="again" kind="Run another test sign-in" />)
                    .getOrElse(() => (
                      <T key="first" kind="Run test sign-in" />
                    ))}
                </Button>
              )}
              {!dirty &&
                allRequiredMatched &&
                onDone
                  .map((done) => (
                    <Button
                      key="done"
                      color="success"
                      disabled={busy}
                      onClick={done}
                      data-intercom-target="sso-diagnostics-done-button"
                    >
                      <T kind="Done" />
                    </Button>
                  ))
                  .getOrElse(() => <React.Fragment key="done" />)}
            </div>
          </div>
        </div>
        <div className="lg:col-span-2">
          <HowThisWorks />
        </div>
      </div>
    </>
  );
};

export default observer(AttributesCheck);
